/**
 * Index intro — animazione di caricamento della home.
 *
 * Le 16 parole dell'indice (che lette in fila sono il manifesto) compaiono unite
 * come una frase (senza numeri romani) con un fade staggerato sx→dx — scoped alle
 * parole (no maschera a schermo). 1,5s dopo, volano alla loro posizione nella
 * griglia (FLIP, ease-out). A corsa finita i numerali compaiono in fade sopra ogni
 * parola e header/footer entrano col wipe a maschera ([data-reveal]).
 *
 * Parte all'arrivo sulla home e a ogni reload, NON tornando alla home da pagine
 * interne: la decisione (reload || mai-vista-in-sessione) è in head.liquid, che
 * setta lo stato pre-paint .off-intro-pending (evita il flash della griglia
 * finale). Qui lo rimuoviamo a fine animazione e marchiamo la sessione.
 *
 * Desktop: frase intera su una riga. Mobile: 4 righe da 4 parole. La griglia
 * finale è identica in entrambi (16 col / 4 col).
 */
(function () {
  'use strict';

  const html = document.documentElement;
  const FLAG = 'off_intro_played';
  // Tempi dai Theme settings (CSS var su :root), con fallback.
  const cssMs = (name, fb) => {
    const v = parseFloat(getComputedStyle(html).getPropertyValue(name));
    return Number.isFinite(v) ? v : fb;
  };
  // Easing bézier: f(t)∈[0,1] dai 4 punti di controllo, come il cubic-bezier CSS ma calcolato in JS
  // (serve per la comparsa-frase, animata a mano via rAF sulla maschera).
  function cubicBezier(x1, y1, x2, y2) {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const fx = (t) => ((ax * t + bx) * t + cx) * t;
    const fy = (t) => ((ay * t + by) * t + cy) * t;
    const dfx = (t) => (3 * ax * t + 2 * bx) * t + cx;
    const solve = (x) => {
      let t = x;
      for (let i = 0; i < 8; i++) {
        const e = fx(t) - x;
        if (Math.abs(e) < 1e-4) return t;
        const d = dfx(t);
        if (Math.abs(d) < 1e-6) break;
        t -= e / d;
      }
      let lo = 0, hi = 1;
      t = x;
      for (let i = 0; i < 20; i++) {
        const e = fx(t) - x;
        if (Math.abs(e) < 1e-4) break;
        if (e > 0) hi = t; else lo = t;
        t = (lo + hi) / 2;
      }
      return t;
    };
    return (t) => (t <= 0 ? 0 : t >= 1 ? 1 : fy(solve(t)));
  }

  const SWEEP = cssMs('--off-intro-sweep', 2800); // comparsa a maschera (smooth)
  const PAUSE = cssMs('--off-intro-pause', 1500); // attesa prima dell'espansione
  const DURATION = cssMs('--off-intro-flight', 1200); // volo parole → griglia
  // Curve di easing dai Theme settings (punti di controllo bézier), con fallback ai default.
  const EASE = `cubic-bezier(${cssMs('--off-intro-flight-ex1', 0.16)}, ${cssMs('--off-intro-flight-ey1', 1)}, ${cssMs('--off-intro-flight-ex2', 0.3)}, ${cssMs('--off-intro-flight-ey2', 1)})`;
  const sweepEase = cubicBezier(cssMs('--off-intro-sweep-ex1', 0.37), cssMs('--off-intro-sweep-ey1', 0), cssMs('--off-intro-sweep-ex2', 0.63), cssMs('--off-intro-sweep-ey2', 1));

  function reveal() {
    html.classList.remove('off-intro-pending');
    try {
      sessionStorage.setItem(FLAG, '1');
    } catch (e) {}
  }

  function bpDesktop() {
    const v = parseFloat(getComputedStyle(html).getPropertyValue('--off-breakpoint-desktop'));
    return Number.isFinite(v) ? v : 1200;
  }

  // Posizioni "frase" iniziali: layout di flusso temporaneo, misurato e rimosso.
  function measureSentence(words) {
    const desktop = window.matchMedia(`(min-width: ${bpDesktop()}px)`).matches;
    const probe = document.createElement('div');
    probe.className = 'main-index-intro-probe';
    probe.setAttribute('aria-hidden', 'true');

    const spans = words.map((text, i) => {
      const span = document.createElement('span');
      span.className = 'off-text-small';
      span.textContent = text;
      probe.appendChild(span);
      if (i < words.length - 1) probe.appendChild(document.createTextNode(' '));
      // Mobile: a capo ogni 4 parole (i 4 gruppi del manifesto).
      if (!desktop && (i + 1) % 4 === 0 && i < words.length - 1) probe.appendChild(document.createElement('br'));
      return span;
    });

    document.body.appendChild(probe);
    const rects = spans.map((s) => s.getBoundingClientRect());
    probe.remove();
    return rects;
  }

  function animate(nav, labels) {
    const finals = labels.map((el) => el.getBoundingClientRect());
    const contents = labels.map((el) => (el.closest('.main-index__content') || el).getBoundingClientRect());
    const starts = measureSentence(labels.map((el) => el.textContent));
    if (starts.some((r) => r.width === 0)) {
      reveal();
      return;
    }

    // Piazza ogni parola alla posizione "frase" (opaca; a nasconderla è la maschera del nav).
    // La label nella cella sta SOTTO il numerale: la frase parte già a quell'altezza (offset del
    // numerale) così durante il volo il testo non scende per far posto ai numeri sopra. 0 se numeri off.
    labels.forEach((el, i) => {
      const numOffset = finals[i].top + finals[i].height / 2 - (contents[i].top + contents[i].height / 2);
      const dx = starts[i].left - finals[i].left;
      const dy = starts[i].top + numOffset - finals[i].top;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.opacity = '1';
    });

    // Comparsa: maschera-gradiente sul nav, animata da INIZIO a FINE del blocco di testo
    // (calcolati dalle posizioni "frase"), NON da bordo a bordo schermo — col testo
    // centrato i lati sono vuoti e i tempi sballerebbero. Bordo sfumato = effetto opacity,
    // movimento continuo e smooth, indipendente dalle singole parole.
    const navLeft = nav.getBoundingClientRect().left;
    const blockLeft = Math.min.apply(null, starts.map((s) => s.left)) - navLeft;
    const blockRight = Math.max.apply(null, starts.map((s) => s.right)) - navLeft;
    const feather = Math.max(120, (blockRight - blockLeft) * 0.22);
    const setMask = (x) => {
      const g = 'linear-gradient(to right, #000 ' + x + 'px, transparent ' + (x + feather) + 'px)';
      nav.style.webkitMaskImage = g;
      nav.style.maskImage = g;
    };
    const from = blockLeft - feather;
    setMask(from);
    void nav.offsetWidth;

    let t0 = null;
    const sweep = (ts) => {
      if (t0 === null) t0 = ts;
      const t = Math.min((ts - t0) / SWEEP, 1);
      setMask(from + (blockRight - from) * sweepEase(t));
      if (t < 1) {
        requestAnimationFrame(sweep);
        return;
      }
      nav.style.webkitMaskImage = '';
      nav.style.maskImage = '';
      setTimeout(flight, PAUSE); // 1,5s di pausa, poi l'espansione
    };
    requestAnimationFrame(sweep);

    function flight() {
      labels.forEach((el) => {
        el.style.transition = `transform ${DURATION}ms ${EASE}`;
        el.style.transform = 'translate(0, 0)';
      });

      // Fine corsa: scopre header/footer (wipe a maschera), rivela i numerali, pulisce inline.
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
        reveal();
        labels.forEach((el) => {
          el.style.transition = '';
          el.style.transform = '';
          el.style.opacity = '';
        });
      };
      labels[labels.length - 1].addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, DURATION + 200);
    }
  }

  function run() {
    if (!html.classList.contains('off-intro-pending')) return;
    const nav = document.querySelector('.main-index:not(.main-index--chapter)');
    const labels = nav ? [...nav.querySelectorAll('.main-index__label')] : [];
    if (!labels.length) {
      reveal();
      return;
    }
    // Misura le posizioni "frase" SOLO dopo che il webfont dei label è DAVVERO caricato: col fallback
    // (Georgia, più largo) la frase parte più spaziata. document.fonts.ready da solo non basta — può
    // risolvere prima che il load del font sia in coda (cache fredda) → serve il load ESPLICITO del
    // font specifico dei label. Safety: se il font non risponde, animo comunque entro 3s (l'intro non
    // deve mai bloccarsi; c'è anche il failsafe a 8s in head.liquid).
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      animate(nav, labels);
    };
    const fonts = document.fonts;
    if (fonts && fonts.load) {
      const cs = getComputedStyle(labels[0]);
      const spec = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      const text = labels.map((el) => el.textContent).join('');
      setTimeout(start, 3000);
      fonts.load(spec, text).then(start, start);
    } else {
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
